import { removeQueryStringParams } from './base/utils'
import { PERSONAL_IDENTIFIABLE_INFORMATION } from './utils/private-data-params'
import { createDebugLogger } from './utils/debug'

// Timing constants
const FALLBACK_NAVIGATION_DELAY = 150
const TRACKING_TIMEOUT = 500

// Configuration for auto-tracking URL patterns
const URL_PATTERNS = {
  '/downloads/mac': 'download',
  '/signup': 'visit_sign_up',
  '/pricing': 'visit_pricing',
}

// Create context-specific debug logger
const debugLog = createDebugLogger('Analytics')

/**
 * Extract element information for debugging
 * @param {Element} element - DOM element
 * @returns {Object} Element info object
 */
const getElementInfo = (element) => ({
  element: element.outerHTML.substring(0, 100) + (element.outerHTML.length > 100 ? '...' : ''),
  text: element.textContent?.trim(),
})

/**
 * Wrapper for Simple Analytics event tracking with debug logging and fallback handling
 * @param {string} eventName - Event name to track
 * @param {Function} callback - Optional callback function
 * @returns {boolean} Whether tracking was attempted
 */
const trackEvent = (eventName, callback = null) => {
  if (typeof window.sa_event !== 'function') {
    debugLog('⚠️', `Simple Analytics not available, executing callback`)
    if (callback) {
      setTimeout(callback, 0)
    }
    return false
  }

  try {
    if (callback) {
      const timeoutId = setTimeout(() => {
        callback()
      }, TRACKING_TIMEOUT)

      window.sa_event(eventName, function () {
        clearTimeout(timeoutId)
        debugLog('✅', `Event "${eventName}" tracked successfully`)
        callback()
      })
    } else {
      window.sa_event(eventName)
      debugLog('✅', `Event "${eventName}" tracked successfully`)
    }
    return true
  } catch (error) {
    debugLog('⚠️', `Simple Analytics tracking failed for "${eventName}":`, error)

    if (callback) {
      setTimeout(callback, 0)
    }
    return false
  }
}

/**
 * Find matching URL pattern for auto-tracking
 * @param {string} href - URL to check
 * @returns {string|null} Event name or null if no match
 */
const getEventNameFromUrl = (href) => {
  const match = Object.entries(URL_PATTERNS).find(([pattern]) => href.includes(pattern))
  return match ? match[1] : null
}

/**
 * Unified click handler for both auto-tracking and manual events
 * @param {Event} event - The click event
 */
const handleClick = (event) => {
  const link = event.target.closest('a[href]')

  // Auto-tracking: Check for navigation links first
  if (link) {
    const href = link.getAttribute('href')
    const autoEventName = href ? getEventNameFromUrl(href) : null

    if (autoEventName) {
      debugLog('🎯', `Auto-detected ${autoEventName} link:`, {
        href,
        ...getElementInfo(link),
      })

      // Always prevent default and track, with guaranteed fallback navigation
      event.preventDefault()

      trackEvent(autoEventName, function () {
        debugLog('🔄', `Navigating to: ${href}`)
        window.location.href = href
      })
      return
    }
  }

  // Manual tracking: Check for data-simple-event attributes
  const manualElement = event.target.closest('[data-simple-event]')
  if (manualElement) {
    const eventName = manualElement.dataset.simpleEvent
    if (eventName) {
      debugLog('✅', `Manual event detected:`, {
        eventName,
        ...getElementInfo(manualElement),
      })
      trackEvent(eventName)
    }
  }
}

/**
 * Initialize Simple Analytics with enhanced tracking
 * Note: Do Not Track (DNT) is automatically respected by Simple Analytics by default
 * @returns {void}
 */
const init = () => {
  // Clean up URL parameters for privacy
  removeQueryStringParams(PERSONAL_IDENTIFIABLE_INFORMATION)

  // Set up unified click handler
  document.addEventListener('click', handleClick)
}

/**
 * Analytics utilities and event tracking
 */
const analytics = {
  init,

  /**
   * Track a custom event using Simple Analytics recommended simple pattern
   * @param {string} eventName - The name of the event (alphanumeric and underscores only)
   */
  track: (eventName) => {
    trackEvent(eventName)
  },

  /**
   * Check if analytics is available
   * @returns {boolean}
   */
  isAvailable: () =>
    typeof window.sa_loaded !== 'undefined' && window.sa_loaded && typeof window.sa_event === 'function',
}

export default analytics
